import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Mail, Phone, Building2, Inbox } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLOR = {
  new: 'bg-cyan-100 text-cyan-800 border-cyan-300',
  contacted: 'bg-amber-100 text-amber-800 border-amber-300',
  qualified: 'bg-purple-100 text-purple-800 border-purple-300',
  converted: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  lost: 'bg-slate-100 text-slate-700 border-slate-300',
};

const AdminPartnerLeadsPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLeads = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/partner-leads`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLeads(data);
    } catch {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateStatus = async (lead_id, status) => {
    try {
      await axios.put(
        `${API}/admin/partner-leads/${lead_id}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Marked as ${status}`);
      fetchLeads();
    } catch {
      toast.error('Update failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-cyan-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <header className="bg-cyan-500 text-white py-6 px-4">
        <div className="max-w-5xl mx-auto">
          <button
            onClick={() => navigate('/admin')}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
            data-testid="back-to-admin-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Dashboard
          </button>
          <h1 className="text-3xl font-black uppercase">B2B Partner Leads</h1>
          <p className="text-cyan-50 mt-1">Inquiries from /for-partners — request your free first report.</p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {leads.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
            <Inbox className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-900 mb-1">No leads yet</h3>
            <p className="text-slate-600">Share <code className="bg-slate-100 px-2 py-0.5 rounded">/for-partners</code> in your outreach to start collecting leads here.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {leads.map((l) => (
              <div
                key={l.lead_id}
                className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm"
                data-testid={`lead-row-${l.lead_id}`}
              >
                <div className="flex items-start justify-between gap-4 mb-2 flex-wrap">
                  <div>
                    <h3 className="text-lg font-black text-slate-900 flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-cyan-600" />
                      {l.org_name}
                      {l.venue_type && (
                        <span className="text-xs font-semibold bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full">
                          {l.venue_type}
                        </span>
                      )}
                    </h3>
                    <p className="text-sm text-slate-600 mt-1">
                      {l.contact_name} · interested in <strong>{l.interest_tier || 'unspecified tier'}</strong>
                    </p>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-bold uppercase border ${
                      STATUS_COLOR[l.status] || STATUS_COLOR.new
                    }`}
                  >
                    {l.status}
                  </span>
                </div>

                <div className="flex flex-wrap gap-3 text-sm text-slate-700 mb-3">
                  <a href={`mailto:${l.contact_email}`} className="inline-flex items-center gap-1 hover:text-cyan-700">
                    <Mail className="w-4 h-4" /> {l.contact_email}
                  </a>
                  {l.contact_phone && (
                    <a href={`tel:${l.contact_phone}`} className="inline-flex items-center gap-1 hover:text-cyan-700">
                      <Phone className="w-4 h-4" /> {l.contact_phone}
                    </a>
                  )}
                  <span className="text-slate-500">
                    {new Date(l.created_at).toLocaleString()}
                  </span>
                </div>

                {l.message && (
                  <blockquote className="text-sm text-slate-700 bg-slate-50 border-l-4 border-cyan-300 p-3 rounded mb-3">
                    {l.message}
                  </blockquote>
                )}

                <div className="flex flex-wrap gap-2">
                  {['contacted', 'qualified', 'converted', 'lost'].map((s) => (
                    <button
                      key={s}
                      onClick={() => updateStatus(l.lead_id, s)}
                      data-testid={`set-${s}-${l.lead_id}`}
                      className={`text-xs font-bold uppercase px-3 py-1.5 rounded-full transition ${
                        l.status === s
                          ? 'bg-cyan-500 text-white'
                          : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      Mark {s}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPartnerLeadsPage;
